import { useLogin } from '@/composables/useAuth'
import { useAuthStore } from '@/store/auth'
import { createSocialLoginProviders } from '@/composables/social-login-providers'
import { ResponseCode } from '@/types/service'
import { toSafeInternalUrl } from '@/utils/common'
import type { SocialLoginType, SocialLoginContext, SocialLoginProvider, SocialRegisterParam, LoginOutcome } from '@/types/socialLogin'

export function useSocialLogin() {
  const { handleLoginSuccess } = useLogin()
  const authStore = useAuthStore()

  const loginProviders = createSocialLoginProviders()
  const getProvider = (type: SocialLoginType): SocialLoginProvider => loginProviders[type]

  const loginWith = async (type: SocialLoginType, ctx: SocialLoginContext): Promise<LoginOutcome> => {
    const provider = getProvider(type)

    try {
      const res = await provider.login(ctx)

      if (res.code === ResponseCode.SUCCESS) {
        await handleLoginSuccess(provider.resolveRedirectUrl?.(ctx) ?? '', provider.registrationType)
        return 'success'
      }

      if (res.code === ResponseCode.UNREGISTERED) {
        authStore.setPendingThirdPartyAuth({
          type,
          accessToken: ctx.token,
          authorizationCode: ctx.authorizationCode,
          name: ctx.name,
          redirectUrl: ctx.redirectUrl,
          fallbackUrl: ctx.fallbackUrl,
        })
        authStore.authLayer = 'third-party-consent'
        authStore.openLoginModal()
        provider.onUnregistered?.(ctx)
        return 'unregistered'
      }

      provider.onError(ctx, res)
      return 'error'
    } catch (error) {
      provider.onError(ctx, error)
      return 'error'
    }
  }

  const registerWith = async (type: SocialLoginType, ctx: SocialLoginContext, param: SocialRegisterParam) => {
    const provider = getProvider(type)
    const res = await provider.register(ctx, param)

    if (res.code === ResponseCode.SUCCESS) {
      const safeRedirectUrl = toSafeInternalUrl(ctx.redirectUrl || ctx.fallbackUrl) ?? '/'
      await handleLoginSuccess(safeRedirectUrl, provider.registrationType, 'register')
      return
    }

    throw res
  }

  return { loginWith, registerWith }
}
